import { current } from '@reduxjs/toolkit';
import { isNullOrUndefined } from '../../utils/helpers';

function handleEnergyFlowAssetTypes(state: any, action: any): void {
    if (action.payload.type === 'setAll') {
        state['assetTypes'] = action.payload.data;
    } else {
        const stateClone = current(state);
        const selectedAssetIndex = stateClone['assetTypes'][action.payload.key]?.findIndex(
            (assetType: any) => assetType?.deviceCategory === action.payload.value
        );

        if (selectedAssetIndex !== -1) {
            state['assetTypes'][action.payload.key][selectedAssetIndex]['isEnabled'] =
                !state['assetTypes'][action.payload.key][selectedAssetIndex]['isEnabled'];
        }
    }
}
function handleAssetTypes(state: any, action: any): void {
    if (action.payload.type === 'setAll') {
        state['assetTypes'] = action.payload.data;
    } else {
        const stateClone = current(state);
        const selectedAssetIndex = stateClone['assetTypes']?.findIndex(
            (assetType: any) => assetType?.deviceCategory === action.payload.value
        );

        if (selectedAssetIndex !== -1) {
            state['assetTypes'][selectedAssetIndex]['isEnabled'] =
                !state['assetTypes'][selectedAssetIndex]['isEnabled'];
        }
    }
}

function addGroupedChannel(payload: any, key: string, item: any): void {
    if (!payload[key]) {
        payload[key] = [];
    }

    const existingRecord = payload[key]?.find((prevItem: any) => prevItem?.deviceId === item?.deviceId);
    if (!existingRecord) {
        payload[key].push({
            deviceId: item.deviceId,
            deviceName: item?.deviceName,
            channels: [item],
        });
        return;
    }

    existingRecord.channels.push(item);
}

function commonWidgetConfiguration(payloadClone: any): any {
    const payload: any = { secondary: [] };

    Object.keys(payloadClone)?.forEach((key: string) => {
        if (Array.isArray(payloadClone[key]) && payloadClone[key]?.length) {
            payloadClone[key]?.forEach((item: any) => addGroupedChannel(payload, key, item));
            return;
        }
        payload[key] = payloadClone[key];
    });

    return payload;
}

function addChannelPayload(state: any, stateKey: string, channelPayload: any): void {
    if (state[stateKey]) {
        state[stateKey]?.push({ ...channelPayload });
    } else {
        state[stateKey] = [{ ...channelPayload }];
    }
}

function channelsHandler(state: any, action: any, keyToTraverse?: string): void {
    const currentData = current(state);
    const stateKey = action.payload.key;
    const traverseKey = keyToTraverse ?? 'channelId';
    const channelPayload = action.payload?.channelPayload;

    const selectedChannelIndex = currentData?.[action.payload.key]?.findIndex(
        (data: any) => data?.[traverseKey] === channelPayload?.[traverseKey]
    );

    const selectedEntityIndex = currentData?.[action.payload.key]?.findIndex(
        (data: any) =>
            data?.deviceId === channelPayload?.deviceId && data[traverseKey] === channelPayload?.[traverseKey]
    );

    const hasMatchingChannel = selectedChannelIndex !== undefined && selectedChannelIndex !== -1;
    const shouldRemoveByChannel = hasMatchingChannel && !action?.payload?.multiDevice;
    const shouldRemoveByEntity = hasMatchingChannel && action?.payload?.multiDevice && selectedEntityIndex !== -1;

    if (shouldRemoveByChannel) {
        state[stateKey]?.splice(selectedChannelIndex, 1);
    } else if (shouldRemoveByEntity) {
        state[stateKey]?.splice(selectedEntityIndex, 1);
    } else {
        addChannelPayload(state, stateKey, channelPayload);
    }

    state[stateKey] = state[stateKey]?.map((item: any, index: number) => ({
        ...item,
        order: index,
    }));
}

function handleReallocateChannels(state: any, action: any): void {
    const movedItem = state[action.payload?.type]?.splice(action.payload?.sourceIndex, 1);
    state[action.payload?.type]?.splice(action.payload.destinationIndex, 0, ...movedItem);
    state[action.payload?.type] = state[action.payload?.type].map((item: any, index: number) => ({
        ...item,
        order: index,
    }));
}

function moveWithinList(state: any, action: any): void {
    const movedItem = state[action.payload.draggableId]?.splice(action.payload.sourceIndex, 1);
    state[action.payload.droppableId].splice(action.payload.destinationIndex, 0, ...movedItem);
    state[action.payload.droppableId] = state[action.payload.droppableId].map((item: any, index: number) => ({
        ...item,
        order: index,
    }));
}

function handleChannelRemove(state: any, action: any, keyToTraverse?: string): void {
    const currentData = current(state);
    if (currentData.widgetTypeId === 'weather') {
        if (currentData[action.payload.type]) {
            state[action.payload.type] = {};
        }
    } else {
        const selectedChannelIndex = currentData[action.payload.type]?.findIndex(
            (data: any) =>
                data[keyToTraverse ?? 'channelId'] + data['deviceId'] ===
                action.payload?.channel[keyToTraverse ?? 'channelId'] + action.payload?.channel['deviceId']
        );
        if (selectedChannelIndex !== -1) {
            state[action.payload.type]?.splice(selectedChannelIndex, 1);
        }
    }
}

function applyDetailToChannelItem(item: any, detail: any): void {
    if (!item || !detail || item.deviceId !== detail.deviceId) {
        return;
    }

    const selectedChannel = detail.channels?.find(
        (payloadChannel: any) => payloadChannel?.channelId === item.channelId
    );

    if (!selectedChannel || selectedChannel.channelId !== item.channelId) {
        return;
    }

    const { records, icon, value, unit } = selectedChannel;

    if (records) {
        item['data'] = records;
    } else {
        if (icon) {
            item['icon'] = icon;
        }
        item['value'] = value;
    }

    item['unit'] = unit;
}

function buildValidChannelsByDevice(details: any): Map<string, Set<string>> {
    const validChannelsByDevice = new Map<string, Set<string>>();

    details?.data?.forEach((detail: any) => {
        if (!detail?.deviceId) return;
        validChannelsByDevice.set(
            detail.deviceId,
            new Set((detail?.channels ?? []).map((channel: any) => channel?.channelId).filter(Boolean))
        );
    });

    return validChannelsByDevice;
}

function reconcileFlatChannelPayload(payloadClone: any, details: any, keys: string[]): any {
    if (!Array.isArray(details?.data)) return payloadClone;

    const validChannelsByDevice = buildValidChannelsByDevice(details);

    keys.forEach((key) => {
        if (!Array.isArray(payloadClone?.[key])) return;

        payloadClone[key] = payloadClone[key].filter((item: any) => {
            const deviceId = item?.deviceId;
            const channelId = item?.channelId;
            if (!deviceId || !channelId) return false;

            const validChannelIds = validChannelsByDevice.get(deviceId);
            return validChannelIds ? validChannelIds.has(channelId) : false;
        });
    });

    return payloadClone;
}

function reconcileDetailChannelPayload(payloadClone: any, details: any): any {
    return reconcileFlatChannelPayload(payloadClone, details, ['primary', 'secondary']);
}

function setChannelDetails(action: any): any {
    const payloadClone = JSON.parse(JSON.stringify(action.payload.channelPayload));
    const channelGroups = ['primary', 'secondary'];

    if (['details', 'properties', 'settings', 'command_bar'].includes(action?.payload?.widgetType)) {
        reconcileDetailChannelPayload(payloadClone, action?.payload?.details);
    } else if (['charts', 'trends'].includes(action?.payload?.widgetType)) {
        reconcileFlatChannelPayload(payloadClone, action?.payload?.details, ['secondary']);
    }

    action?.payload?.details?.data?.forEach((detail: any) => {
        channelGroups.forEach((key: string) => {
            payloadClone[key]?.forEach((item: any) => applyDetailToChannelItem(item, detail));
        });
    });
    return payloadClone;
}

function handleGroupDevicesFilter(state: any, action: any): any {
    const currentState = current(state);

    if (Array.isArray(action.payload.value)) {
        state[action.payload.key] = action.payload.value;
    } else {
        const selectedIndex = currentState[action.payload.key]?.findIndex(
            (item: any) => item.id === action.payload?.value?.id
        );
        if (selectedIndex !== -1 && !isNullOrUndefined(selectedIndex)) {
            if (currentState[action.payload.key]?.length === 1) {
                state['secondary'] = [];
            }
            state[action.payload.key].splice(selectedIndex, 1);
        } else {
            if (isNullOrUndefined(currentState[action.payload.key])) {
                state[action.payload.key] = [action.payload.value];
            } else {
                state[action.payload.key].push(action.payload.value);
            }
        }
    }
}

export {
    commonWidgetConfiguration,
    channelsHandler,
    handleAssetTypes,
    handleEnergyFlowAssetTypes,
    setChannelDetails,
    handleChannelRemove,
    handleGroupDevicesFilter,
    handleReallocateChannels,
    moveWithinList,
};
